import { useMemo, useState } from "react";
import { TellescopeModelFromName, useTellescopeInfiniteQuery } from "@tellescope/tanstack-query";
import { Label, Popover } from "@tellescope/design-system";
import {
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  useStateWithSetAction,
} from "@tellescope/ui";
import { useDebouncedValue } from "@tellescope/react-hooks";
import { useTranslation } from "react-i18next";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@tellescope/utils";
import { TellescopeCustomFilterProps, TellescopeFormFieldBaseProps } from "../types";
import { handleCustomFilter } from "../utils";
import { FieldDescription } from "../FieldDescription";

export interface ConditionsFieldProps
  extends TellescopeFormFieldBaseProps<"Conditions">,
    TellescopeCustomFilterProps<TellescopeModelFromName<"diagnosis_codes">> {}

export const ConditionsField = ({
  title,
  description,
  htmlDescription,
  placeholder,
  value,
  onChange,
  className,
  filterOptions: conditions,
  customFilter,
  onBlur,
  error,
  required,
  autoFocus,
}: ConditionsFieldProps) => {
  const { t } = useTranslation("forms");
  const [open, setOpen] = useStateWithSetAction(false, (isOpen) => {
    if (!isOpen) {
      onBlur?.();
    }
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery] = useDebouncedValue(searchQuery);
  const conditionsQuery = useTellescopeInfiniteQuery({
    model: "diagnosis_codes",
    method: "getSome",
    request: {
      search:
        debouncedSearchQuery && !customFilter
          ? {
              query: debouncedSearchQuery,
            }
          : undefined,
    },
    select: (data) => data.pages.flat(),
    enabled: conditions === undefined,
  });

  const filteredConditions = useMemo(() => {
    const resolved = handleCustomFilter({
      filterOptions: conditions,
      customFilter,
      debouncedSearchQuery,
      fallbackOptions: conditionsQuery?.data ?? [],
      getStrings: (item) => [item.code, item.display],
    });

    const seenDisplays = new Set<string>();
    return resolved.filter((condition) => {
      if (seenDisplays.has(condition.display)) return false;
      seenDisplays.add(condition.display);
      return true;
    });
  }, [conditions, conditionsQuery.data, customFilter, debouncedSearchQuery]);

  return (
    <div className="space-y-1">
      <Label required={required}>{title || placeholder || t("Choose a condition...")}</Label>
      <FieldDescription description={description} htmlDescription={htmlDescription} />
      <Popover open={open} onOpenChange={setOpen}>
        <Popover.Trigger asChild className={className}>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between font-normal"
            autoFocus={autoFocus}
          >
            {value ? (
              <div className="flex flex-col gap-1 items-start overflow-hidden w-full text-left">
                <div className="font-medium text-sm truncate w-full">
                  {value.map((value) => value.display).join(", ")}
                </div>
              </div>
            ) : (
              <span className="text-muted-foreground">{placeholder ?? t("Choose a condition...")}</span>
            )}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </Popover.Trigger>
        <Popover.Content className="w-[var(--radix-popover-trigger-width)] p-0">
          <Command shouldFilter={false}>
            <CommandInput
              value={searchQuery}
              onValueChange={setSearchQuery}
              placeholder={t("Search conditions...")}
              className="h-9"
            />
            <CommandList>
              <CommandEmpty>
                {conditionsQuery.isLoading && conditions === undefined
                  ? t("Loading conditions...")
                  : t("No conditions found")}
              </CommandEmpty>
              <CommandGroup>
                {filteredConditions?.map((condition) => {
                  const isSelected = !!value?.some((item) => item.code === condition.code);
                  return (
                    <CommandItem
                      key={condition.id}
                      value={condition.code}
                      onSelect={() => {
                        if (isSelected) {
                          onChange(value?.filter((item) => item.code !== condition.code) ?? []);
                        } else {
                          onChange([
                            ...(value ?? []),
                            { code: condition.code, display: condition.display, system: condition.system },
                          ]);
                        }
                      }}
                    >
                      <div className="flex flex-col gap-1 w-full">
                        <div className="font-medium text-sm truncate">{condition.display}</div>
                        <div className="text-xs text-muted-foreground truncate">{condition.code}</div>
                      </div>
                      <Check className={cn("ml-auto h-4 w-4 shrink-0", isSelected ? "opacity-100" : "opacity-0")} />
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </Popover.Content>
      </Popover>
      {!!error && <p className="text-sm text-destructive mt-1">{error}</p>}
    </div>
  );
};
